<template>
    <div class="hotel-order-summary">
        <div class="hotel-order-summary__header d-flex mb-4">
            <v-img
                tile
                :src="$filters.imageUrl(offer.thumb, 350)"
                :alt="offer.name"
                eager
                width="100"
                max-width="100"
                height="100"
                :lazy-src="$filters.srcHost('/placeholder.png')"
                class="me-2"
                @error="onImgError"
            >
                <template v-slot:placeholder>
                    <v-row v-show="!imgError && offer.thumb" class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey-lighten-5" />
                    </v-row>
                </template>
            </v-img>
            <div class="hotel-order-summary__header-content ma-2 flex-grow-1 flex-shrink-1 text-truncate">
                <div class="d-flex align-center flex-wrap">
                    <hotel-stars :hotel="offer" />
                    <hotel-type-label class="ms-1" :offer="offer" />
                    <v-img
                        v-if="offer && offer.recommended && showPreferred"
                        :src="$filters.srcHost('/preferred.png')"
                        width="80px"
                        class="preferred-thumb flex-grow-0 mx-1"
                    />
                </div>
                <div class="hotel-order-summary__header-name font-weight-bold text-truncate" :title="offer.name">
                    {{ offer.name }}
                </div>
                <supplier-name-label v-if="isTO1" :supplier-name="offer.supplierName" />
                <hotel-address-label
                    :address="offer.address"
                    :class="{'cursor-pointer': offer.coordinates && offer.coordinates.length}"
                    @click.native="mapModal = true"
                />
            </div>
        </div>
        <slot name="summary" />
        <hotel-info-time class="mb-3" />
        <div
            v-for="(room, roomIndex) in roomOffers"
            :key="roomIndex"
            class="hotel-order-summary__content relative-position"
        >
            <div
                class="hotel-order-summary__content-title d-flex flex-wrap align-center font-weight-bold cursor-pointer mb-1"
                @click="roomInfo(room)"
            >
                <v-icon size="small" start>mdi-information-outline</v-icon>
                <span class="mr-2">{{ room.name }}</span>
                <supplier-name-label v-if="isTO1" :supplier-name="room.hotelSupplierName" />
                <ProductOfferTipsDialog v-if="room.tipsAvailable" :offer-key="room.groupedOffers[0].offerKey" />
                <v-tooltip v-if="room.packageOnly" :text="$t('dynamic_rate')" location="bottom">
                    <template v-slot:activator="{props}">
                        <v-icon class="ms-1" v-bind="props" color="primary"> mdi-sync </v-icon>
                    </template>
                    <span>{{ $t('dynamic_rate') }}</span>
                </v-tooltip>
            </div>
            <div class="hotel-order-summary__icons text-caption">
                <hotel-available-label :room="room" />
                <hotel-meal-type-label
                    :hotel="offer"
                    :room="room"
                    :selected-meal-types="selectedMealTypes[roomIndex]"
                />
                <tariff-label :room="room" :hotel="offer" />
                <conditions-label
                    :offer-key="room.groupedOffers[0].offerKey"
                    :price="room.price"
                    :offer="offer"
                    @conditions="showConditionsInfo(room)"
                />
            </div>
            <div v-if="showPrice" class="room-price mt-1">
                <room-price-label :price="roomPrice(roomIndex)" :room="room" />
                <commission-amount :commission="room.price.commission" />
                <net-amount :price="room.price" :search-request="searchRequest" />
                <rate-note :room="room" />
                <v-tooltip v-if="isShowDynamicRate" :text="$t('dynamic_rate')" location="bottom">
                    <template v-slot:activator="{props}">
                        <v-icon v-bind="props" color="primary"> mdi-chart-line </v-icon>
                    </template>
                    <span>{{ $t('dynamic_rate') }}</span>
                </v-tooltip>
            </div>
            <div v-else-if="free" class="mt-1 font-weight-bold">{{ $t('free') }}</div>
            <div
                v-if="
                    selectedMealTypes.length &&
                    selectedMealTypes[roomIndex].filter(({price}) => price.amount !== 0).length
                "
                class="d-block my-4 px-0"
            >
                <div class="font-weight-bold mb-2">
                    {{ $t('meal_type') }}
                </div>
                <div
                    v-for="({name, quantity, price}, index) in selectedMealTypes[roomIndex].filter(
                        ({price}) => price.amount !== 0
                    )"
                    :key="index"
                    class="py-0 d-flex text-caption"
                >
                    <span>{{ name }}</span>
                    <span v-if="quantity > 1">&nbsp;* {{ quantity }} </span>
                    <v-spacer />
                    <span class="font-weight-bold">
                        {{ $filters.price({amount: price.amount * quantity, currency: price.currency}) }}
                    </span>
                </div>
            </div>
            <extra-services-summary
                v-if="extraServices[roomIndex] && additionalOptions[roomIndex]"
                :extra-services="extraServices[roomIndex]"
                :additional-options="additionalOptions[roomIndex]"
            />
            <v-divider v-if="roomCheckInOutExtraServices(roomIndex).length" class="mb-4" />
            <div v-if="roomCheckInOutExtraServices(roomIndex).length" class="d-block py-4 px-0">
                <v-row class="flex-column">
                    <v-col
                        v-for="service in roomCheckInOutExtraServices(roomIndex)"
                        :key="service.time + service.text"
                        class="py-0 d-flex align-center text-caption"
                    >
                        <span>{{ `${$t(service.text)} ` }}{{ service.time }}</span>
                        <v-spacer />
                        <span>{{ $filters.price(service.price) }}</span>
                    </v-col>
                </v-row>
            </div>
            <v-divider v-if="roomIndex < roomOffers.length - 1" class="my-4" />
            <slot name="removeBtn" :offer-key="room.groupedOffers[0].offerKey" />
        </div>
        <add-ons-summary-list :selected-add-ons="selectedAddOns" />
        <client-only>
            <v-dialog v-if="offer.coordinates && offer.coordinates.length" v-model="mapModal" :max-width="500">
                <map-location v-if="mapModal" :offer="offer" :height="`500px`" :city="city" />
            </v-dialog>
        </client-only>
    </div>
</template>

<script lang="ts">
    import {Component, mixins, Prop} from 'vue-facing-decorator'
    import CommissionAmount from '~/components/snippets/CommissionAmount.vue'
    import ConditionsLabel from '~src/components/snippets/conditionsLabel.src'
    import {EventBus, SHOW_HOTEL_CONDITIONS_INFO, SHOW_ROOM_INFO_MODAL_EVENT} from '@/utils/event-bus'
    import HotelMealTypeLabel from '~src/components/hotels/snippets/hotelMealTypeLabel.src'
    import OriginalCurrencyNotification from '@/components/booking/snippets/OriginalCurrencyNotification.vue'
    import CorporatePolicyViolation from '~/components/snippets/CorporatePolicyViolation.vue'
    import RateNote from '~src/components/snippets/RateNote.vue'
    import OrderSummaryLayout from '~src/components/booking/orderSummaryLayout.src'
    import TariffLabel from '~src/components/hotels/snippets/tariffLabel.src'
    import HotelStars from '~src/components/hotels/snippets/hotelStars.src'
    import HotelAvailableLabel from '~src/components/hotels/snippets/hotelAvailableLabel.src'
    import HotelTypeLabel from '@/components/hotels/snippets/HotelTypeLabel.vue'
    import HotelOwnLabel from '~src/components/hotels/snippets/HotelOwnLabel.vue'
    import HotelAddressLabel from '~src/components/hotels/snippets/hotelAddressLabel.src'
    import ExtraServicesSummary from '@/components/booking/snippets/ExtraServicesSummary.vue'
    import RoomPriceLabel from '~src/components/hotels/booking/snippets/roomPriceLabel.src'
    import HotelInfoTime from '@/components/info/HotelInfoTime.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import NetAmount from '~src/components/snippets/NetAmount.vue'
    import MapLocation from '~src/components/info/mapLocation.src'
    // import AddOnsSummaryList from '@/components/booking/addOns/AddOnsSummaryList.vue'
    import {OrderSummaryBase} from '@/components/booking/OrderSummaryBase.vue'
    import {HotelStoreMixin} from '@/mixins/HotelStoreMixin.vue'
    import {clone} from '@/utils/helpers'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'

    @Component({
        components: {
            ProductOfferTipsDialog,
            AddOnsSummaryList: defineAsyncComponent(() => import('@/components/booking/addOns/AddOnsSummaryList.vue')),
            HotelInfoTime,
            RoomPriceLabel,
            ExtraServicesSummary,
            OrderSummaryLayout,
            HotelStars,
            RateNote,
            OriginalCurrencyNotification,
            HotelMealTypeLabel,
            ConditionsLabel,
            CommissionAmount,
            CorporatePolicyViolation,
            TariffLabel,
            HotelAvailableLabel,
            HotelTypeLabel,
            HotelOwnLabel,
            HotelAddressLabel,
            SupplierNameLabel,
            NetAmount,
            MapLocation,
        },
    })
    export class HotelOrderSummary extends mixins(OrderSummaryBase, HotelStoreMixin) {
        @Prop({required: true}) offer
        @Prop({required: true}) roomOffers
        @Prop({required: true}) searchRequest
        @Prop({required: true, type: Array}) selectedAddOns
        @Prop({required: true, type: Array}) additionalOptions
        @Prop({required: true, type: Array}) selectedMealTypes

        imgError = false
        mapModal = false

        onImgError() {
            this.imgError = true
            return false
        }

        roomPrice(roomIndex) {
            const price =
                this.prebooked && this.roomOffers[roomIndex]?.currentPrice
                    ? clone(this.roomOffers[roomIndex].currentPrice)
                    : clone(this.roomOffers[roomIndex].price)
            const mandatory =
                hotelsStore.bookingAdditionalOptions[roomIndex]?.extraServices?.filter(({mandatory}) => mandatory) || []
            if (mandatory.length) {
                mandatory.forEach(item => {
                    price.amount -= item.salesTerms.find(el => el.type === 'CLIENT').price.amount
                    price.originalAmount -= item.salesTerms.find(el => el.type === 'CLIENT').originalAmount
                })
            }
            return price
        }

        showConditionsInfo(room) {
            EventBus.$emit(SHOW_HOTEL_CONDITIONS_INFO, {searchRequest: this.searchRequest, hotel: this.offer, room})
        }

        roomCheckInOutExtraServices(roomIndex) {
            return hotelsStore.checkInOutExtraService[roomIndex]
                ? Object.values(hotelsStore.checkInOutExtraService[roomIndex])
                : []
        }

        roomInfo(room) {
            EventBus.$emit(SHOW_ROOM_INFO_MODAL_EVENT, {hotel: this.offer, room})
        }

        get showPreferred() {
            return false
        }

        get productStore() {
            return hotelsStore
        }

        get showPrice() {
            return !this.free
        }

        get isTO1() {
            return authStore.isTO1
        }

        get city() {
            return hotelsRuntimeStore.city
        }
    }

    export default HotelOrderSummary
</script>
